import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiHome, FiSun, FiMoon, FiArrowLeft, FiPhone, FiUser, FiLoader } = FiIcons;

const RegisterForm = ({ signUp, setCurrentView }) => {
  const { isDark, toggleTheme } = useTheme();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showAdminPopup, setShowAdminPopup] = React.useState(false);
  const [adminPassword, setAdminPassword] = React.useState('');

  const handleRegister = (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (isAdmin) {
      // Show admin password popup
      setShowAdminPopup(true);
      return;
    }

    // Regular user registration
    createUser(false);
  };

  const handleAdminConfirm = () => {
    if (adminPassword !== 'HomeScout') {
      setError('Password amministratore non valida');
      return;
    }
    setShowAdminPopup(false);
    setAdminPassword('');
    createUser(true);
  };

  const createUser = async (isAdminUser) => {
    setLoading(true);
    setError('');

    try {
      const userData = {
        phone_number: phoneNumber,
        role: isAdminUser ? 'admin' : 'segnalatore'
      };

      const { data, error } = await signUp(email, password, userData);

      if (error) {
        if (error.message.includes('already registered')) {
          setError('Email già registrata');
        } else if (error.message.includes('Password should be at least')) {
          setError('La password deve essere di almeno 6 caratteri');
        } else {
          setError(error.message);
        }
      } else if (data.user && !data.user.email_confirmed_at) {
        setError('Ti abbiamo inviato un\'email di conferma. Controlla la tua casella di posta.');
      }
      // Success is handled by the auth state change in App.jsx
    } catch (err) {
      setError('Si è verificato un errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const closeAdminPopup = () => {
    setShowAdminPopup(false);
    setAdminPassword('');
    setError('');
  };

  return (
    <>
      <div className={`min-h-screen transition-colors ${isDark ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-blue-100'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className={`w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl overflow-hidden`}>
            {/* Header */}
            <div className={`${isDark ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white p-8 text-center relative`}>
              <button
                onClick={() => setCurrentView('landing')}
                className={`absolute top-4 left-4 p-2 rounded-lg ${isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-500 hover:bg-blue-400'} transition-colors`}
                title="Torna alla home"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              </button>

              <button
                onClick={toggleTheme}
                className={`absolute top-4 right-4 p-2 rounded-lg ${isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-500 hover:bg-blue-400'} transition-colors`}
              >
                <SafeIcon icon={isDark ? FiSun : FiMoon} className="w-4 h-4" />
              </button>

              <div className={`inline-flex p-4 rounded-full mb-4 ${isDark ? 'bg-blue-700' : 'bg-blue-500'}`}>
                <SafeIcon icon={FiHome} className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold mb-2">HomeScout</h1>
              <p className="text-blue-100">Unisciti alla nostra community</p>
            </div>

            {/* Form */}
            <div className="p-8">
              <h2 className={`text-2xl font-bold mb-6 text-center ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Crea il tuo account
              </h2>

              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Email
                  </label>
                  <div className="relative">
                    <SafeIcon 
                      icon={FiMail} 
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      placeholder="inserisci@email.com"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Password
                  </label>
                  <div className="relative">
                    <SafeIcon 
                      icon={FiLock} 
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
                    />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      placeholder="••••••••"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Numero di telefono
                  </label>
                  <div className="relative">
                    <SafeIcon 
                      icon={FiPhone} 
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
                    />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      placeholder="+39 123 456 7890"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className={`p-4 rounded-lg border-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      disabled={loading}
                    />
                    <div>
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        Sono un amministratore
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Richiede password speciale per la verifica
                      </div>
                    </div>
                  </label>
                </div>

                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                      <span>Registrazione in corso...</span>
                    </>
                  ) : (
                    <span>Registrati</span>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setCurrentView('login')}
                  className={`text-sm font-medium hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                >
                  Hai già un account? Accedi
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Password Popup */}
      {showAdminPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-8 rounded-2xl shadow-2xl w-96 mx-4`}>
            <div className="text-center mb-6">
              <div className={`inline-flex p-4 rounded-full mb-4 ${isDark ? 'bg-blue-800' : 'bg-blue-100'}`}>
                <SafeIcon icon={FiUser} className={`w-8 h-8 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} />
              </div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Conferma Amministratore
              </h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
                Inserisci la password per creare un nuovo account amministratore
              </p>
            </div>

            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Password Amministratore
              </label>
              <div className="relative">
                <SafeIcon 
                  icon={FiLock} 
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
                />
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  }`}
                  placeholder="Inserisci password amministratore"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-center">
                {error}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={handleAdminConfirm}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50"
              >
                Conferma
              </button>
              <button
                onClick={closeAdminPopup}
                disabled={loading}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                  isDark 
                    ? 'bg-gray-600 text-white hover:bg-gray-500' 
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisterForm;